import { useEffect, useRef, useState } from "react";
import { Wallet2 } from "lucide-react";

export default function CardWalletSetup({ api, cardId, language, disabled, onDetails }) {
  const ru = language === "ru";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  const alive = useRef(true);
  const sending = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const path = `/cards/${encodeURIComponent(cardId)}/wallet-activation`;
  const receive = data => {
    const statuses = ["idle", "busy", "starting", "unknown", "armed", "cooldown", "code_received"];
    if (!statuses.includes(data.status) || !Number.isFinite(data.serverNow)) throw new Error("invalid session");
    return { ...data, deadline: Number.isFinite(data.expiresAt) ? Date.now() + (data.expiresAt - data.serverNow) * 1000 : null };
  };
  const start = async () => {
    if (sending.current || disabled) return;
    sending.current = true; setOpen(true); setBusy(true); setError(false);
    try {
      const response = await api.post(path, {}, { timeout: 35000 });
      if (alive.current) setSession(receive(response.data));
    } catch (_) {
      // A timed-out POST may have armed the card. Read its durable status;
      // never automatically repeat the activation request.
      if (alive.current) { setError(true); setSession({ status: "unknown" }); }
    } finally {
      sending.current = false;
      if (alive.current) setBusy(false);
    }
  };
  useEffect(() => {
    if (!open || busy || disabled) return undefined;
    let cancelled = false, timer;
    const poll = async () => {
      try {
        const response = await api.get(path, { timeout: 10000 });
        if (!cancelled) { setSession(receive(response.data)); setError(false); }
      } catch (_) { if (!cancelled) setError(true); }
      if (!cancelled) timer = setTimeout(poll, 10000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [api, path, open, busy, disabled]);
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  const status = session?.status;
  const waiting = open && status && status !== "idle";
  const seconds = session?.deadline ? Math.max(0, Math.ceil((session.deadline - now) / 1000)) : 0;
  const countdown = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  let message = "";
  if (status === "armed") message = ru ? `Добавьте карту в Wallet в течение ${countdown}.` : `Add the card to Wallet within ${countdown}.`;
  if (status === "code_received") message = ru ? "Код получен. Проверьте личные сообщения бота LightPay и почту, указанную при выпуске карты. Завершите подтверждение в Wallet." : "Code received. Check your private LightPay bot chat and the email used to issue the card. Complete verification in Wallet.";
  if (status === "busy") message = ru ? "Подключение временно занято. Дождитесь доступности кнопки и начните снова." : "Wallet setup is temporarily busy. Wait until the button becomes available, then try again.";
  if (status === "cooldown") message = ru ? "Время для добавления истекло. Через несколько секунд можно будет начать снова." : "The setup window has expired. You can start again shortly.";
  if (["starting", "unknown"].includes(status)) message = ru ? "Уточняем, включено ли ожидание кода. Пока не добавляйте карту в Wallet. Если статус долго не меняется, обратитесь в поддержку." : "Checking whether code capture is ready. Please wait before adding the card to Wallet. Contact support if this status persists.";
  const title = ru ? "Подключить Apple Pay" : "Set up Apple Pay";
  return <section className="lp-glass space-y-3 p-3.5" data-testid="wallet-setup">
    <button type="button" className="lp-press lp-tile flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-3 py-3 text-[13px] font-semibold disabled:opacity-50" disabled={disabled || busy || Boolean(waiting)} onClick={start} data-testid="wallet-setup-start" aria-expanded={open}>
      <Wallet2 className="h-4 w-4" />{busy ? (ru ? "Подготавливаем…" : "Preparing…") : title}
    </button>
    {open && <div className="space-y-3 text-[12px] leading-relaxed" data-testid="wallet-setup-instructions">
      {message && <p role="status" className="text-link" data-testid="wallet-setup-status">{message}</p>}
      {error && <p role="alert" className="text-destructive-fg">{ru ? "Не удалось обновить статус. Проверяем повторно; не запрашивайте новый код пока идёт проверка." : "Unable to refresh status. Checking again; please wait before requesting another code."}</p>}
      <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
        <li>{ru ? "После подготовки откройте реквизиты карты в LightPay." : "Once ready, open your card details in LightPay."}</li>
        <li>{ru ? "На iPhone откройте Wallet → «+» → дебетовая или кредитная карта → ввод данных вручную." : "On your iPhone, open Wallet → + → Debit or Credit Card → Enter Card Details Manually."}</li>
        <li>{ru ? "Введите данные карты. Если Wallet запросит код, возьмите его из личных сообщений бота LightPay или почты и завершите подтверждение." : "Enter the card details. If Wallet requests a code, use the code from your private LightPay bot chat or email to complete verification."}</li>
      </ol>
      <button type="button" className="lp-press text-link underline underline-offset-4" onClick={onDetails} disabled={disabled}>{ru ? "Открыть реквизиты карты" : "Open card details"}</button>
      <p className="text-muted-foreground">{ru ? "Карта подключена только после подтверждения в приложении Wallet." : "Your card is connected only after Wallet confirms setup."}</p>
    </div>}
  </section>;
}
